import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { visuallyHidden } from '@mui/utils';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { ResizableTableCell } from './resizableTableCell/resizableTableCell';
import { EmbersCheckbox } from '../../../checkbox/checkbox';
import { embersTableHeadTheme, HEADER_CELL_PADDING } from './tableHeadStyles';
import { TableHeadScrollHandler } from './tableHeadScrollHandler';
import { EmbersMenuLaunchButton } from '../../../menu/menuLaunchButton';
import { InteractiveIcon } from '../../../interactiveIcon';
import { DEFAULT_WIDTH } from '../../utils/resizeCellUtils';
import { useWindowSize } from '../../../utils/resizeHook';
export function EmbersTableHead(props) {
  var onSelectAllClick = props.onSelectAllClick,
    order = props.order,
    orderBy = props.orderBy,
    numSelected = props.numSelected,
    rowCount = props.rowCount,
    onRequestSort = props.onRequestSort,
    defaultHeadCells = props.defaultHeadCells,
    headCells = props.headCells,
    eyeColumn = props.eyeColumn,
    onEyeColumnClick = props.onEyeColumnClick,
    eyeColumnMenuIsOpen = props.eyeColumnMenuIsOpen,
    onTableScrolled = props.onTableScrolled,
    unclickable = props.unclickable,
    checked = props.checked,
    checkboxDisabled = props.checkboxDisabled,
    tableCollectionString = props.tableCollectionString,
    selectAllCheckboxAriaLabel = props.selectAllCheckboxAriaLabel,
    scrollContainerClass = props.scrollContainerClass,
    tableHeadId = props.id,
    onResizeEnd = props.onResizeEnd,
    ariaLiveOn = props.ariaLiveOn;
  var hasCheckboxColumn = numSelected !== undefined;
  var intl = useIntl();
  var _useWindowSize = useWindowSize(),
    _useWindowSize2 = _slicedToArray(_useWindowSize, 1),
    windowWidth = _useWindowSize2[0];
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    tableHeight = _useState2[0],
    setTableHeight = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    cloneNeedsRefresh = _useState4[0],
    setCloneNeedsRefresh = _useState4[1];
  var cloneRefreshTimeoutRef = useRef(undefined);
  useEffect(function () {
    // clean up in case the user (or test) exits during timeout
    return function () {
      if (cloneRefreshTimeoutRef.current) {
        clearTimeout(cloneRefreshTimeoutRef.current);
        cloneRefreshTimeoutRef.current = undefined;
      }
    };
  }, []);
  var toggleCloneNeedsRefresh = function toggleCloneNeedsRefresh() {
    setCloneNeedsRefresh(true);
    // TODO figure out a reliable non-timeout way, if there is one,
    //  to do this reset of cloneNeedsRefresh
    cloneRefreshTimeoutRef.current = setTimeout(function () {
      setCloneNeedsRefresh(false);
    }, 200);
  };
  var createSortHandler = function createSortHandler(property) {
    return function (event) {
      if (onRequestSort) {
        onRequestSort(event, property);
        toggleCloneNeedsRefresh();
      }
    };
  };
  var onMouseEnter = function onMouseEnter(e) {
    var _e$currentTarget$pare;
    //@ts-ignore
    setTableHeight(((_e$currentTarget$pare = e.currentTarget.parentElement) === null || _e$currentTarget$pare === void 0 ? void 0 : _e$currentTarget$pare.clientHeight) || 0);
  };
  var handleEyeMenuOpenRequest = function handleEyeMenuOpenRequest(buttonElem) {
    onEyeColumnClick({
      currentTarget: buttonElem
    });
  };
  var handleEyeMenuCloseRequest = function handleEyeMenuCloseRequest() {
    onEyeColumnClick({
      currentTarget: null
    });
  };

  // headCells changes/window width resizes/tableHeight changes need to refresh fixed table head
  useEffect(function () {
    toggleCloneNeedsRefresh();
  }, [headCells.length, headCells, windowWidth, tableHeight]);
  var getNewOrder = function getNewOrder() {
    var foundItem = headCells.find(function (item) {
      return item.id === orderBy;
    });
    var isOrderByDate = foundItem === null || foundItem === void 0 ? void 0 : foundItem.isDate;
    if (!isOrderByDate) {
      return order;
    }
    return order === 'desc' ? 'asc' : 'desc';
  };

  // eslint-disable-next-line react/display-name
  var renderOrderIcon = function renderOrderIcon(id) {
    return function () {
      var classname = "EmbersTable-Header-SortIcon-root".concat(id === orderBy ? ' EmbersTable-Header-SortIcon-active' : '');
      var newOrder = getNewOrder();
      return newOrder === 'desc' ? /*#__PURE__*/React.createElement(InteractiveIcon, {
        tabIndex: -1,
        className: classnames('EmbersTable-Header-SortIcon-Desc', classname),
        iconName: "icon-arrow-down",
        label: intl.formatMessage({
          id: 'embers.table.iconLabel.sortColumn',
          defaultMessage: 'Sort column'
        }),
        ariaProps: {
          'aria-hidden': 'true'
        }
      }) : /*#__PURE__*/React.createElement(InteractiveIcon, {
        tabIndex: -1,
        className: classnames('EmbersTable-Header-SortIcon-Asc', classname),
        iconName: "icon-arrow-up",
        label: intl.formatMessage({
          id: 'embers.table.iconLabel.sortColumn',
          defaultMessage: 'Sort column'
        }),
        ariaProps: {
          'aria-hidden': 'true'
        }
      });
    };
  };
  var renderHeader = function renderHeader(headRef, headClassName, headStyle, fixedWidths, id) {
    return /*#__PURE__*/React.createElement(TableHead, {
      id: id || tableHeadId,
      ref: headRef,
      onMouseEnter: onMouseEnter,
      className: headClassName,
      style: headStyle
    }, /*#__PURE__*/React.createElement(TableRow, null, hasCheckboxColumn && /*#__PURE__*/React.createElement(ResizableTableCell, {
      padding: "checkbox",
      key: "checkbox",
      resizable: false,
      width: DEFAULT_WIDTH.checkboxWidth,
      className: "fixed-checkbox"
      // setting the component as TD (not TH) helps accessibility because
      //  screen readers otherwise prepend the checkbox's aria-label to
      //  the announcement of every row's checkbox, which is tedious
      ,
      component: "td"
    }, /*#__PURE__*/React.createElement(EmbersCheckbox, {
      color: "primary",
      indeterminate: numSelected > 0,
      checked: rowCount > 0 && checked,
      disabled: unclickable || checkboxDisabled,
      onChange: onSelectAllClick,
      inputProps: {
        'aria-label': selectAllCheckboxAriaLabel !== null && selectAllCheckboxAriaLabel !== void 0 ? selectAllCheckboxAriaLabel : intl.formatMessage({
          id: 'embers.table.checkbox.selectAll',
          defaultMessage: 'Select all {items}'
        }, {
          items: tableCollectionString
        })
      }
    })), headCells.map(function (_ref, index) {
      var id = _ref.id,
        width = _ref.width,
        minWidth = _ref.minWidth,
        icon = _ref.icon,
        label = _ref.label,
        isDate = _ref.isDate,
        _ref$sortable = _ref.sortable,
        sortable = _ref$sortable === void 0 ? true : _ref$sortable,
        align = _ref.align;
      var cellContent = /*#__PURE__*/React.createElement(React.Fragment, null, icon ? /*#__PURE__*/React.createElement("span", {
        className: "EmbersTable-Header-icon"
      }, icon) : /*#__PURE__*/React.createElement("span", {
        className: "EmbersTable-Header-title"
      }, label));
      var cellWidth = width;
      if (fixedWidths) {
        cellWidth = fixedWidths[index];
      }
      var clearSortInfoForSR = function clearSortInfoForSR(element) {
        if (id === orderBy) {
          var srOnlyElem = element.querySelector('.Embers-srOnly');
          if (srOnlyElem) {
            srOnlyElem.innerHTML = '';
          }
        }
      };
      var useSortLabel = sortable && !unclickable;
      var noSortLabel = !sortable || unclickable;
      var showResizeBar = index < headCells.length - 1;
      return /*#__PURE__*/React.createElement(ResizableTableCell, {
        key: id,
        sortDirection: orderBy === id ? order : false,
        className: classnames(icon ? 'EmbersTable-HeaderCell-icon' : 'EmbersTable-HeaderCell-title', id !== orderBy ? 'EmbersTable-HeaderCell-sortHidden' : 'EmbersTable-HeaderCell-sortActive', {
          'EmbersTable-HeaderCell-noSortLabel': noSortLabel
        }),
        headId: tableHeadId,
        hasCheckboxColumn: hasCheckboxColumn,
        index: index,
        resizable: showResizeBar,
        onResizeEnd: onResizeEnd,
        width: cellWidth,
        align: align,
        horizontalPadding: HEADER_CELL_PADDING[icon ? 'icon' : 'title'].left + HEADER_CELL_PADDING[icon ? 'icon' : 'title'].right,
        tableHeight: tableHeight,
        onMouseEnter: function onMouseEnter(e) {
          clearSortInfoForSR(e.target);
        },
        onKeyUp: function onKeyUp(e) {
          if (e.key === 'Tab') {
            clearSortInfoForSR(e.target);
          }
        },
        defaultHeadCells: defaultHeadCells,
        headCells: headCells
      }, useSortLabel && /*#__PURE__*/React.createElement(TableSortLabel, {
        active: orderBy === id,
        direction: orderBy === id ? order : 'desc',
        onClick: createSortHandler(id),
        IconComponent: renderOrderIcon(id),
        "aria-label": "".concat(label === null || label === void 0 ? void 0 : label.toLocaleUpperCase(), " ").concat(sortable ? intl.formatMessage({
          id: 'embers.table.iconLabel.sortColumn',
          defaultMessage: 'Sort column'
        }) : '')
      }, cellContent, /*#__PURE__*/React.createElement(Box, {
        component: "span",
        className: "Embers-srOnly",
        sx: visuallyHidden,
        textTransform: 'none',
        role: "status",
        "aria-live": ariaLiveOn ? 'polite' : 'off'
      }, orderBy === id ? getNewOrder() === 'desc' ? intl.formatMessage({
        id: 'embers.table.checkbox.sortDesc',
        defaultMessage: 'Sorted descending'
      }) : intl.formatMessage({
        id: 'embers.table.checkbox.sortAsc',
        defaultMessage: 'Sorted ascending'
      }) : '')), noSortLabel && cellContent);
    }), eyeColumn && /*#__PURE__*/React.createElement(ResizableTableCell, {
      padding: "checkbox",
      key: "eye-column",
      resizable: false,
      width: DEFAULT_WIDTH.eyeWidth,
      className: "fixed-eye",
      headId: tableHeadId
    }, /*#__PURE__*/React.createElement(EmbersMenuLaunchButton, {
      onOpenMenuRequest: handleEyeMenuOpenRequest,
      onCloseMenuRequest: handleEyeMenuCloseRequest,
      menuIsOpen: eyeColumnMenuIsOpen,
      tabIndex: 0,
      className: "eyeColumnLaunchButton",
      disabled: unclickable,
      label: intl.formatMessage({
        id: 'embers.table.iconLabel.columnVisibility',
        defaultMessage: 'Show/hide table columns'
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "icon-visibility"
    })))));
  };
  var tableHeadRef = useRef(null);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: embersTableHeadTheme
  }, /*#__PURE__*/React.createElement(TableHeadScrollHandler, {
    id: "".concat(tableHeadId, "-fixed"),
    renderHeader: renderHeader,
    needsRerender: cloneNeedsRefresh,
    tableHeadElem: tableHeadRef.current,
    onTableScrolled: onTableScrolled,
    scrollContainerClass: scrollContainerClass
  }, renderHeader(tableHeadRef, classnames('EmbersTableHead-root', {
    'EmbersTableHead-unclickable': unclickable,
    'EmbersTableHead-hasCheckboxColumn': hasCheckboxColumn
  }))));
}